import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "hasCloseButton", "isOpen", "onClose", "use"],
  _excluded2 = ["paddingInlineStart"],
  _excluded3 = ["children", "color", "fontSize"],
  _excluded4 = ["children"],
  _excluded5 = ["children", "align", "paddingInlineEnd"];
import styled from 'styled-components';
import { useState, createContext, useContext } from 'react';
import { Modal as FoundationsModal } from 'foundations-components/transitional/components/Modal';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { useSpacingProps } from '../Layout/useSpacingProps';
import { LayoutBox } from '../Layout/LayoutBox';
import { headingFontSizeMap } from '../Typography/typography';

/**
 * Trellis Modal has opinionated CSS for all h5 tags.
 * @todo Restore the heading styles from the Foundations Modal
 * */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledHeading = styled(LayoutBox).withConfig({
  displayName: "Modal__StyledHeading",
  componentId: "sc-3p5nnb-0"
})(["", ";"], ({
  theme,
  $fontSize = 'md'
}) => `
    font-size: ${theme.typography[headingFontSizeMap[$fontSize]].fontSize};
    font-weight: ${theme.typography[headingFontSizeMap[$fontSize]].fontWeight};
    line-height: ${theme.typography[headingFontSizeMap[$fontSize]].lineHeight};
  `);
export const useModal = (defaultIsOpen = false) => {
  const [isModalOpen, setIsModalOpen] = useState(defaultIsOpen);
  const handleModalToggle = () => setIsModalOpen(!isModalOpen);
  return {
    isModalOpen,
    handleModalToggle
  };
};
export const ModalContext = /*#__PURE__*/createContext({});
export const Modal = _ref => {
  let {
      children,
      hasCloseButton = true,
      isOpen,
      onClose,
      use = 'default'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (!isOpen) {
    return null;
  }
  return /*#__PURE__*/_jsx(ModalContext.Provider, {
    value: {
      use
    },
    children: /*#__PURE__*/_jsxs(FoundationsModal, Object.assign({}, rest, {
      use: use,
      children: [hasCloseButton ? /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }) : null, children]
    }))
  });
};
const ModalHeader = _ref2 => {
  let {
      paddingInlineStart: providedPaddingInlineStart
    } = _ref2,
    restWithSpacingProps = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    use
  } = useContext(ModalContext);
  let paddingInlineStart = providedPaddingInlineStart;
  if (use === 'conversational') {
    paddingInlineStart = providedPaddingInlineStart || 'space';
  }
  const {
    spacingProps,
    otherProps: rest
  } = useSpacingProps(Object.assign({}, restWithSpacingProps, {
    paddingInlineStart
  }));
  return /*#__PURE__*/_jsx(DialogHeader, Object.assign({}, rest, spacingProps));
};
Modal.Header = ModalHeader;
const levelMap = {
  xxxs: '1',
  xxs: '1',
  xs: '2',
  sm: '3',
  md: '4',
  lg: '5',
  xl: '6',
  xxl: '6',
  xxxl: '6'
};
const ModalHeading = _ref3 => {
  let {
      children,
      color: providedColor,
      fontSize
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const {
    use
  } = useContext(ModalContext);
  const color = use !== 'conversational' ? 'inverse' : providedColor;
  return /*#__PURE__*/_jsx(StyledHeading, Object.assign({}, rest, {
    $fontSize: fontSize,
    "aria-level": fontSize ? levelMap[fontSize] : 4,
    color: color,
    role: "heading",
    children: children
  }));
};
Modal.Heading = ModalHeading;
const ModalBody = _ref4 => {
  let {
      children
    } = _ref4,
    restWithSpacingProps = _objectWithoutPropertiesLoose(_ref4, _excluded4);
  const {
    spacingProps,
    otherProps: rest
  } = useSpacingProps(Object.assign({}, restWithSpacingProps));
  return /*#__PURE__*/_jsx(DialogBody, Object.assign({}, rest, spacingProps, {
    children: children
  }));
};
Modal.Body = ModalBody;
const ModalFooter = _ref5 => {
  let {
      children,
      align: providedAlign,
      paddingInlineEnd: providedPaddingInlineEnd
    } = _ref5,
    restWithSpacingProps = _objectWithoutPropertiesLoose(_ref5, _excluded5);
  const {
    use
  } = useContext(ModalContext);
  let paddingInlineEnd = providedPaddingInlineEnd;
  let align = providedAlign;
  if (use === 'conversational') {
    paddingInlineEnd = providedPaddingInlineEnd || 'space-lg';
    align = providedAlign || 'center';
  }
  const {
    spacingProps,
    otherProps: rest
  } = useSpacingProps(Object.assign({}, restWithSpacingProps, {
    paddingInlineEnd
  }));
  return /*#__PURE__*/_jsx(DialogFooter, Object.assign({}, rest, spacingProps, {
    align: align,
    children: children
  }));
};
Modal.Footer = ModalFooter;